import { createGraphFromSteps, parseYAML, Step } from "~/lib/pipeline";
import useTemplate from "~/components/Playground/useTemplate";
import { useMemo } from "react";
import { useSteps } from "~/components/build/Show/lib/useSteps";
import { StepDependencyContext } from "~/components/build/Show/lib/types";

export interface CreateGraphOptions {
  removeIgnoredSteps?: boolean;
  expandedGroupSteps?: Set<string>;
}

/**
 * Create a ReactFlow graph from steps and dependencies.
 *
 * This is a pure function that can be used outside of React hooks,
 * for example in admin views that receive data as props.
 */
export function createGraph(
  steps: Step[],
  dependenciesByStep: Record<Step["uuid"], StepDependencyContext>,
  options: CreateGraphOptions = {},
) {
  const { removeIgnoredSteps = true, expandedGroupSteps } = options;

  const stepsWithValidDependencies = steps.filter((step) => Boolean(dependenciesByStep[step.uuid]));

  return createGraphFromSteps(stepsWithValidDependencies, dependenciesByStep, {
    removeIgnoredSteps,
    expandedGroupSteps,
    flattenImplicitGroupChildrenDependencies: true,
  });
}

/**
 * Create a ReactFlow graph from pipeline YAML
 */
export const useGraphFromTemplate = ({
  templateID,
  expandedGroupSteps,
  removeIgnoredSteps = true,
}: {
  templateID?: string;
  removeIgnoredSteps?: boolean;
  expandedGroupSteps?: Set<string>;
}) => {
  const { yaml } = useTemplate(templateID);

  return useMemo(() => {
    if (yaml) {
      const { steps, dependenciesByStep } = parseYAML(yaml);
      return createGraphFromSteps(steps, dependenciesByStep, {
        removeIgnoredSteps,
        flattenImplicitGroupChildrenDependencies: true,
      });
    }

    return { nodes: [], edges: [] };
  }, [yaml, removeIgnoredSteps, expandedGroupSteps]);
};

/**
 * Create a ReactFlow graph from build steps
 */
export const useGraph = ({
  expandedGroupSteps,
  dependenciesByStep,
  removeIgnoredSteps = true,
}: {
  removeIgnoredSteps?: boolean;
  expandedGroupSteps?: Set<string>;
  dependenciesByStep: Record<Step["uuid"], StepDependencyContext>;
}) => {
  const { steps } = useSteps();

  return useMemo(() => {
    return createGraph(steps, dependenciesByStep, {
      removeIgnoredSteps,
      expandedGroupSteps,
    });
  }, [steps, dependenciesByStep, removeIgnoredSteps, expandedGroupSteps]);
};
